import { useId, useMemo, useState, type FormEvent } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { ApiError } from "../../lib/api.js";
import { createSale } from "../../lib/api-modules/accounts.js";
import type {
  AccountLotDetail,
  CostBasisMethod,
  CreateSaleInput,
  SaleResult,
} from "../../lib/api-modules/types.js";
import { centsToDollars, dollarsToCents, formatCentsCurrency } from "../../lib/util/money.js";
import { ErrorMessage } from "../auth/error-message.js";

/**
 * <SaleForm> — sell N shares of a single ticker (Phase 3 Session 3).
 *
 * Inputs:
 *   - Quantity (numeric, default = full holding qty, max = current qty)
 *   - Sale price per share (dollar input; converted to cents at submit)
 *   - Sale date (default = today, locale-naive)
 *   - Lot picker — shown ONLY when account.cost_basis_method ===
 *     "specific". Order matters: lots reduced in checkbox-toggle order
 *     until sale qty exhausted.
 *
 * On success: parent rerenders via React Query invalidation. The toast
 * with realized G/L + LT/ST split is rendered inline above the form's
 * close button (until a global toast component lands in a later phase).
 */

interface SaleFormProps {
  readonly accountId: string;
  readonly ticker: string;
  readonly availableQuantity: number;
  readonly lots: readonly AccountLotDetail[];
  readonly costBasisMethod: CostBasisMethod | null;
  readonly onClose: () => void;
}

export function SaleForm({
  accountId,
  ticker,
  availableQuantity,
  lots,
  costBasisMethod,
  onClose,
}: SaleFormProps) {
  const queryClient = useQueryClient();
  const isSpecific = costBasisMethod === "specific";
  const qtyId = useId();
  const priceId = useId();
  const dateId = useId();

  const [quantity, setQuantity] = useState<string>(
    availableQuantity.toString(),
  );
  const [salePrice, setSalePrice] = useState<string>("");
  const [saleDate, setSaleDate] = useState<string>(todayIsoDate());
  // Order-preserving selection — Set wouldn't keep insertion-order
  // semantically; we use an array of lot_ids and a derived Set for
  // membership checks.
  const [selectedLotIds, setSelectedLotIds] = useState<string[]>([]);
  const [inlineError, setInlineError] = useState<string | null>(null);
  const [result, setResult] = useState<SaleResult | null>(null);

  const selectedSet = useMemo(
    () => new Set(selectedLotIds),
    [selectedLotIds],
  );

  // Live realized-PnL preview for the lot picker. Best-effort only —
  // the actual server-side computation is the source of truth and may
  // differ by ≤N cents on average-method rounding. For specific method
  // this preview is exact.
  const livePreview = useMemo(() => {
    if (!isSpecific) return null;
    const qty = Number(quantity);
    const priceCents = parseDollarStringToCents(salePrice);
    if (!(qty > 0) || priceCents == null) return null;
    let remaining = qty;
    let realizedCents = 0;
    for (const id of selectedLotIds) {
      const lot = lots.find((l) => l.lot_id === id);
      if (!lot) continue;
      const reduce = Math.min(lot.quantity, remaining);
      if (reduce <= 0) break;
      const fraction = reduce / lot.quantity;
      const cb = Math.round((lot.cost_basis_cents ?? 0) * fraction);
      realizedCents += Math.round(reduce * priceCents) - cb;
      remaining -= reduce;
    }
    return { realized_pnl_cents: realizedCents, covered: qty - remaining };
  }, [isSpecific, lots, quantity, salePrice, selectedLotIds]);

  const mutation = useMutation({
    mutationFn: async (): Promise<SaleResult> => {
      const qty = Number(quantity);
      const priceCents = dollarsToCents(salePrice);
      const input: CreateSaleInput = {
        transaction_type: "sell",
        ticker,
        quantity: qty,
        sale_price_cents: priceCents,
        sale_date: saleDate,
        ...(isSpecific && selectedLotIds.length > 0
          ? { lot_ids: selectedLotIds }
          : {}),
      };
      return createSale(accountId, input);
    },
    onSuccess: async (saleResult) => {
      setResult(saleResult);
      await queryClient.invalidateQueries({ queryKey: ["accounts"] });
      // Holdings refetch is delayed because the recompute now runs via
      // the lots-table DDB stream (Phase 3.4); 800ms covers the median
      // streams replay tail. The realized-G/L summary stays visible.
      setTimeout(() => {
        void queryClient.invalidateQueries({
          queryKey: ["accounts", accountId, "holdings"],
        });
      }, 800);
      // Don't auto-close — let the user see the realized G/L summary.
    },
    onError: (err) => {
      setInlineError(
        err instanceof ApiError
          ? `${err.message}${err.code ? ` (code: ${err.code})` : ""}`
          : "Couldn't record sale. Try again.",
      );
    },
  });

  function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setInlineError(null);
    const qty = Number(quantity);
    if (!(qty > 0)) {
      setInlineError("Quantity must be greater than zero.");
      return;
    }
    if (qty > availableQuantity) {
      setInlineError(
        `Quantity ${qty} exceeds available ${availableQuantity}.`,
      );
      return;
    }
    if (salePrice.trim().length === 0) {
      setInlineError("Sale price is required.");
      return;
    }
    if (saleDate.length === 0) {
      setInlineError("Sale date is required.");
      return;
    }
    if (isSpecific && selectedLotIds.length === 0) {
      setInlineError(
        "Pick at least one lot — this account uses specific-ID cost basis.",
      );
      return;
    }
    mutation.mutate();
  }

  function toggleLot(lotId: string) {
    setSelectedLotIds((prev) =>
      prev.includes(lotId)
        ? prev.filter((id) => id !== lotId)
        : [...prev, lotId],
    );
  }

  return (
    <form
      onSubmit={onSubmit}
      aria-label={`Sell ${ticker}`}
      className="flex flex-col gap-3 rounded-md border border-zinc-200 bg-zinc-50/40 p-3 dark:border-zinc-800 dark:bg-zinc-900/40"
    >
      <header className="flex items-center justify-between">
        <h4 className="text-sm font-semibold">Sell {ticker}</h4>
        <button
          type="button"
          onClick={onClose}
          className="text-xs text-zinc-500 hover:underline"
          disabled={mutation.isPending}
        >
          Cancel
        </button>
      </header>

      <p className="text-xs text-zinc-500">
        Available: {availableQuantity.toLocaleString("en-US", { maximumFractionDigits: 4 })} shares ·
        Method: <span className="font-medium">{costBasisMethod ?? "average"}</span>
      </p>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <div>
          <label htmlFor={qtyId} className="mb-1 block text-xs font-medium">
            Quantity
          </label>
          <input
            id={qtyId}
            type="number"
            className={inputClass}
            value={quantity}
            step="0.0001"
            min="0"
            max={availableQuantity}
            onChange={(e) => setQuantity(e.target.value)}
            disabled={mutation.isPending || result !== null}
            autoFocus
          />
        </div>
        <div>
          <label htmlFor={priceId} className="mb-1 block text-xs font-medium">
            Sale price ($/share)
          </label>
          <input
            id={priceId}
            type="text"
            inputMode="decimal"
            className={inputClass}
            value={salePrice}
            placeholder="0.00"
            onChange={(e) => setSalePrice(e.target.value)}
            disabled={mutation.isPending || result !== null}
          />
        </div>
        <div>
          <label htmlFor={dateId} className="mb-1 block text-xs font-medium">
            Sale date
          </label>
          <input
            id={dateId}
            type="date"
            className={inputClass}
            value={saleDate}
            onChange={(e) => setSaleDate(e.target.value)}
            disabled={mutation.isPending || result !== null}
          />
        </div>
      </div>

      {isSpecific && (
        <div className="rounded-md border border-zinc-200 p-2 dark:border-zinc-800">
          <p className="mb-2 text-xs font-medium">
            Pick lots (in order of consumption):
          </p>
          <ul className="flex flex-col gap-1">
            {lots
              .filter((l) => l.quantity > 0)
              .map((lot, idx) => {
                const checked = selectedSet.has(lot.lot_id);
                const orderIndex = selectedLotIds.indexOf(lot.lot_id);
                return (
                  <li
                    key={lot.lot_id}
                    className="flex items-center gap-2 text-xs"
                  >
                    <input
                      id={`lot-${idx}`}
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggleLot(lot.lot_id)}
                      disabled={mutation.isPending || result !== null}
                    />
                    <label
                      htmlFor={`lot-${idx}`}
                      className="flex-1 tabular-nums"
                    >
                      {orderIndex >= 0 && (
                        <span className="mr-1 inline-block min-w-[1.5rem] text-zinc-500">
                          #{orderIndex + 1}
                        </span>
                      )}
                      <span className="text-zinc-600 dark:text-zinc-400">
                        {lot.purchase_date}
                      </span>{" "}
                      ·{" "}
                      {lot.quantity.toLocaleString("en-US", {
                        maximumFractionDigits: 4,
                      })}{" "}
                      shares · cost{" "}
                      {formatCentsCurrency(lot.cost_basis_cents)}{" "}
                      {lot.unit_cost_cents !== null && (
                        <span className="text-zinc-500">
                          (${centsToDollars(lot.unit_cost_cents)}/sh)
                        </span>
                      )}
                    </label>
                  </li>
                );
              })}
          </ul>
          {livePreview && (
            <p className="mt-2 text-xs text-zinc-600 dark:text-zinc-400">
              Live preview: covers{" "}
              {livePreview.covered.toLocaleString("en-US", {
                maximumFractionDigits: 4,
              })}{" "}
              shares · realized G/L{" "}
              <span
                className={
                  livePreview.realized_pnl_cents >= 0
                    ? "text-emerald-700 dark:text-emerald-300"
                    : "text-rose-700 dark:text-rose-300"
                }
              >
                {formatCentsCurrency(livePreview.realized_pnl_cents)}
              </span>
            </p>
          )}
        </div>
      )}

      {result ? (
        <div
          className="rounded-md border border-emerald-300 bg-emerald-50 p-3 text-sm text-emerald-900 dark:border-emerald-700 dark:bg-emerald-950 dark:text-emerald-100"
          role="status"
        >
          <p className="font-medium">
            Sold {Number(quantity).toLocaleString("en-US", { maximumFractionDigits: 4 })} shares of{" "}
            {ticker}.
          </p>
          <p className="mt-1 tabular-nums">
            Realized G/L:{" "}
            <span className="font-semibold">
              {formatCentsCurrency(result.realized_pnl_cents)}
            </span>{" "}
            <span className="text-xs">
              (LT {formatCentsCurrency(result.lt_pnl_cents)} / ST{" "}
              {formatCentsCurrency(result.st_pnl_cents)})
            </span>
          </p>
          <button
            type="button"
            onClick={onClose}
            className="mt-2 rounded-md bg-emerald-700 px-3 py-1.5 text-xs font-medium text-white hover:bg-emerald-800"
          >
            Done
          </button>
        </div>
      ) : (
        <div className="flex items-center gap-2">
          <button
            type="submit"
            disabled={mutation.isPending}
            className="rounded-md bg-brand-500 px-3 py-1.5 text-xs font-medium text-white shadow-sm transition hover:bg-brand-600 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {mutation.isPending ? "Selling…" : "Submit sale"}
          </button>
          <button
            type="button"
            onClick={onClose}
            disabled={mutation.isPending}
            className="rounded-md border border-zinc-300 px-3 py-1.5 text-xs hover:bg-zinc-50 dark:border-zinc-700 dark:hover:bg-zinc-900"
          >
            Cancel
          </button>
        </div>
      )}

      {inlineError && <ErrorMessage>{inlineError}</ErrorMessage>}
    </form>
  );
}

const inputClass =
  "w-full rounded-md border border-zinc-300 px-2 py-1.5 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 dark:border-zinc-700 dark:bg-zinc-900 disabled:cursor-not-allowed disabled:opacity-60";

function todayIsoDate(): string {
  const d = new Date();
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}

function pad2(n: number): string {
  return n < 10 ? `0${n}` : `${n}`;
}

/**
 * Parse a dollar string (e.g. "1,234.56" or "100") to integer cents.
 * Returns `null` on empty/invalid — used by the live-preview which
 * shouldn't throw on partial input.
 */
function parseDollarStringToCents(s: string): number | null {
  if (s.trim().length === 0) return null;
  const cleaned = s.replace(/[$,]/g, "").trim();
  const n = Number(cleaned);
  if (!Number.isFinite(n)) return null;
  return Math.round(n * 100);
}
